import React, { useRef, useState } from "react";
import Image from "next/image";
import { UserProfile } from "@/lib/types";
import { updateUserProfileAPI } from "@/actions/user_specific";
import { getDeviceId } from "@/lib/analytics/helpers";
import { useAuthUserContext } from "@/contexts/authUserContext";
import { uploadImageAPI } from "@/actions/backend";
import Link from "next/link";

interface Props {
    open: boolean;
    onClose: () => void;
    user_info: (UserProfile & {
        post_count: number;
    }) | undefined;
}

export default function UserProfileActions({
    open,
    onClose,
    user_info,
}: Props) {
    const [editProfile, setEditProfile] = useState(false);
    const [profile, setProfile] = useState({
        name: user_info?.name || '',
        user_name: user_info?.user_name || '',
        bio: user_info?.bio || '',
        photo_url: user_info?.photo_url || ''
    })
    const [isUploadingProfilePhoto, setIsUploadingProfilePhoto] = useState(false);
    const [isUpdatingProfileData, setIsUpdatingProfileData] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>()

    const {user, device} = useAuthUserContext()

    async function updateProfileData(){
        try {
            setIsUpdatingProfileData(true);
            const user_token = await user?.getIdToken();
            await updateUserProfileAPI(profile, {
                user_id: user_info?.id,
                user_token,
                device_token: device?.auth_token,
                child_user_id: (user as any)?.child_user_id,
                device_id: getDeviceId()
            });
        } catch (e: any){
            console.error(e);
            setErrorMessage(e.message || 'Something went wrong!')
        } finally {
            setIsUpdatingProfileData(false);
        }
    }

    async function uploadProfilePhoto(e: React.ChangeEvent<HTMLInputElement>){
        const file = e.target.files?.[0];
        if(file){
            setIsUploadingProfilePhoto(true)
            const file_type = file.type.split("/")[0];
            if(file_type == "image"){
                const image = await uploadImageAPI(file)
                setProfile(prev => ({...prev, photo_url: image}))
            } else {
                setErrorMessage("Unsupported File type "+(file_type||"."))
            }
            setIsUploadingProfilePhoto(false)
        }
    }

    const fileRef = useRef<HTMLInputElement | null>(null);

    if (!open) return null;

    return (
            <div
                className="fixed inset-0 bg-black/10 flex items-center justify-center z-50"
                onClick={onClose}
            >
                <section
                    className="bg-white rounded-md w-11/12 max-w-md p-5 shadow-lg max-h-[90vh] overflow-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {editProfile ? (
                    <div className="text-gray-600 text-sm">
                                    <div className="relative mb-8">
                                        <Image
                                            src={
                                                (profile.photo_url || "/default-profile.png")
                                            }
                                            width={96}
                                            height={96}
                                            className="w-24 h-24 rounded-full object-cover"
                                            onClick={() => fileRef.current?.click()}
                                            alt="profile"
                                        />
                            <span
                                className="absolute bottom-0 left-16 bg-white rounded-full p-1 text-xs text-gray-700 cursor-pointer"
                                onClick={() => fileRef.current?.click()}
                            >
                                {isUploadingProfilePhoto
                                    ? "Uploading Profile Picture"
                                    : "Change Profile Photo"}
                            </span>
                                            <input
                                                ref={fileRef}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={uploadProfilePhoto}
                                            />
                        </div>

                        <div className="mb-2">
                            <label className="block">Name</label>
                            <input
                                value={profile.name}
                                onChange={(e) => setProfile(prev => ({...prev, name: e.target.value}))}
                                className="w-full border-b border-gray-300 py-1 outline-none text-sm"
                            />
                        </div>

                        <div className="mb-2">
                            <label className="block">Username</label>
                            <input
                                value={profile.user_name}
                                onChange={(e) => setProfile(prev => ({...prev, user_name: e.target.value}))}
                                className="w-full border-b border-gray-300 py-1 outline-none text-sm"
                            />
                        </div>

                        <div className="mb-2">
                            <label className="block">Bio</label>
                            <input
                                value={profile.bio}
                                onChange={(e) => setProfile(prev => ({...prev, bio: e.target.value}))}
                                className="w-full border-b border-gray-300 py-1 outline-none text-sm"
                            />
                        </div>

                        {errorMessage && (
                            <div className="text-red-500 text-xs mb-2">Updating Error. Please Try Again</div>
                        )}

                        <div className="flex justify-end mt-4">
                            <button
                                className="px-4 py-2 border rounded bg-white text-gray-600 mr-2 disabled:opacity-70"
                                onClick={() => setEditProfile(false)}
                                disabled={!!isUpdatingProfileData || !!isUploadingProfilePhoto}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-4 py-2 rounded bg-[#D10846] text-white disabled:opacity-70"
                                onClick={updateProfileData}
                                disabled={!!isUpdatingProfileData || !!isUploadingProfilePhoto}
                            >
                                {isUpdatingProfileData ? "Updating" : "Confirm"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="text-gray-600 text-sm">
                                    <div className="flex items-center mb-4">
                                        <Image
                                            src={user_info?.photo_url || "/default-profile.png"}
                                            width={96}
                                            height={96}
                                            className="w-24 h-24 rounded-full object-cover"
                                            alt="profile"
                                        />
                            <div className="ml-4">
                                <div className="font-medium text-gray-900">{user_info?.name}</div>
                                <div className="text-sm text-gray-500">@{user_info?.user_name}</div>
                            </div>
                            <div className="ml-auto">
                                <button
                                    className="text-[#D10846] text-sm"
                                    onClick={() => setEditProfile(true)}
                                >
                                    Edit my profile
                                </button>
                            </div>
                        </div>

                        <div className="flex flex-col">
                            <Link className="border border-gray-200 text-gray-600 rounded py-2 mb-2 text-center" href="/logout">Sign Out</Link>
                            <Link className="border border-gray-200 text-gray-600 rounded py-2 mb-2 text-center" href="/contact-us">Delete Account</Link>
                            <Link className="border border-gray-200 text-gray-600 rounded py-2 mb-2 text-center" href="/change-password">Change Password</Link>
                            <button className="border border-gray-200 text-gray-600 rounded py-2 text-center cursor-pointer" onClick={onClose}>Cancel</button>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}